import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { getStockItems, getStockItem, getUntrackedProducts, createStockItem, canWriteInventory, STOCK_STATUSES } from "../lib/inventory";
import StockItemDetail, { StockStatusBadge } from "./StockItemDetail";

const PAGE_SIZE = 25;
const n3 = (x) => (Math.round((Number(x) || 0) * 1000) / 1000).toLocaleString();

function Th({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  return (
    <th style={S.th} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

function AddStockItemModal({ onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [reorder, setReorder] = useState(0);
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { (async () => { try { const { data } = await getUntrackedProducts(); setProducts(data); } catch (e) { setError(e.message); } })(); }, []);

  const save = async () => {
    if (!productId) { setError("Choose a product."); return; }
    setBusy(true); setError("");
    try { await createStockItem({ product_id: productId, reorder_level: Number(reorder) || 0, warehouse_location: location }); onSaved(); }
    catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 460 }}>
        <h2 style={{ margin: "0 0 14px", fontSize: 18, fontWeight: 700 }}>Add Stock Item</h2>
        <label style={S.label}>Product *</label>
        <select style={S.input} value={productId} onChange={(e) => setProductId(e.target.value)} aria-label="Product *">
          <option value="">{products.length ? "Select a product…" : "All products are already tracked"}</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ""}</option>)}
        </select>
        <label style={{ ...S.label, marginTop: 10 }}>Reorder level</label>
        <input style={S.input} type="number" value={reorder} onChange={(e) => setReorder(e.target.value)} />
        <label style={{ ...S.label, marginTop: 10 }}>Warehouse location</label>
        <input style={S.input} value={location} placeholder="e.g. Aisle 3 / Bay B" onChange={(e) => setLocation(e.target.value)} />
        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 12 }} role="alert">{error}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
          <button style={S.btn("secondary")} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={save} disabled={busy}>Add</button>
        </div>
      </div>
    </div>
  );
}

export default function InventoryManager({ autoNew, onAutoNew }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  const [detail, setDetail] = useState(null);
  const [adding, setAdding] = useState(false);

  // P4-CC15b-2b: stock_items/stock_movements direct writes are owner/admin-only
  // (createStockItem/updateStockItem). Default FALSE — fail closed — so nobody sees
  // "+ Add Stock Item" until the database has actually confirmed they may use it.
  // An RPC error is treated the same as "not permitted", never as "permitted".
  const [canWrite, setCanWrite] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => { try { const ok = await canWriteInventory(); if (alive) setCanWrite(ok); } catch { if (alive) setCanWrite(false); } })();
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data, count: total } = await getStockItems({ search, status, page, pageSize: PAGE_SIZE, sortBy, sortDir }); setRows(data); setCount(total); }
    catch (e) { setError(e.message || "Could not load inventory."); }
    finally { setLoading(false); }
  }, [search, status, sortBy, sortDir, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (autoNew && canWrite) { setAdding(true); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew, canWrite]);

  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v); setPage(1); };
  const onSort = (col) => { setPage(1); if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir("asc"); } };
  const clearFilters = () => { setSearch(""); setStatus("all"); setPage(1); };

  const openDetail = async (r) => { try { setDetail(await getStockItem(r.id)); } catch (e) { setError(e.message); } };
  const refreshDetail = async () => { if (detail) { try { setDetail(await getStockItem(detail.id)); } catch { /* ignore */ } } await load(); };

  // dashboard mini-stats from the current page is misleading; show filtered totals instead
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || status !== "all";
  const selStyle = { ...S.input, width: "auto", minWidth: 150 };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search SKU, product, location…" value={search} onChange={(e) => onSearch(e.target.value)} aria-label="Search inventory" />
        <select style={selStyle} value={status} onChange={(e) => onStatus(e.target.value)} aria-label="Filter by stock status">
          <option value="all">All stock</option>
          {STOCK_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{count} item{count === 1 ? "" : "s"}</span>
        {canWrite && <button style={S.btn()} onClick={() => setAdding(true)}>+ Add Stock Item</button>}
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={load}>Retry</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th label="SKU" col="sku" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Product" col="name" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Current" col="current" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Available" col="available" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Reserved</th>
              <th style={S.th}>Reorder</th>
              <Th label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={7} style={S.empty}>
                {hasFilters ? (
                  <div>No stock items match your filters.<div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={clearFilters}>Clear filters</button></div></div>
                ) : (
                  <div>
                    <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No inventory yet</div>
                    <div style={{ marginBottom: 12 }}>Receive a Goods Received Note{canWrite ? ", or add a stock item to set reorder levels." : "."}</div>
                    {canWrite && <button style={S.btn()} onClick={() => setAdding(true)}>+ Add Stock Item</button>}
                  </div>
                )}
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => openDetail(r)}>
                <td style={S.td}><span style={{ fontFamily: "monospace", fontSize: 12, color: C.textMuted }}>{r.sku || "—"}</span></td>
                <td style={S.td}>{r.name || <span style={{ color: C.textDim }}>—</span>}</td>
                <td style={{ ...S.td, fontWeight: 600 }}>{n3(r.current_stock)}</td>
                <td style={{ ...S.td, color: C.success, fontWeight: 600 }}>{n3(r.available_stock)}</td>
                <td style={S.td}>{r.reserved_stock > 0 ? <span style={{ color: C.warning }}>{n3(r.reserved_stock)}</span> : "—"}</td>
                <td style={S.td}>{n3(r.reorder_level)}</td>
                <td style={S.td}><StockStatusBadge status={r.stock_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>

        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {detail && <StockItemDetail item={detail} onClose={() => setDetail(null)} onChanged={refreshDetail} />}
      {adding && <AddStockItemModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}
